import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import { AuthContext } from '../components/AuthProvider';
import { get_bearers } from "../tools-network";
import { showTemporaryMessage } from "../components/Message";

const ListOfBearers = ({
		loginToken,
		userId,
		setSelectedBearerId,
		reportBearerName,
		signalNoBearersAvailable,
		signalBearerListLoaded,
		refreshBearerListTrigger,
		isEditingBearer,
		reportLoadingState}) => {

	const { t } = useTranslation();

	const { handleInvalidToken } = React.useContext(AuthContext);

	const [bearers, setBearers] = useState([]);
	const [selectedBearerData, setSelectedBearerData] = useState(null);
	const [isLoading, setIsLoading] = useState(true);

	const updateListOfBearers = async () => {
		setIsLoading(true);
		if (reportLoadingState) {
			reportLoadingState(true);
		}
		try {
			const res = await get_bearers(loginToken, userId);
			if(res.status) {
				const updateBearers = res.data;
				// The format from the server is
				//	 {1:"bearer 1",2:"bearer 2"}
				// but we need a list to Listbox.Options like:
				//	[{id:1,name:"bearer 1"},{id:2,name:"bearer 2"}]
				const bearerList = Object.entries(updateBearers).map(([id, name]) => ({ id, name }));
				setBearers(bearerList);
				// Re-select the previously selected bearer if it still exists
				if (selectedBearerData) {
					const stillExists = bearerList.find(bearer => bearer.id === selectedBearerData.id);
					if (stillExists) {
						setSelectedBearerData(stillExists);
						setSelectedBearerId(stillExists.id);
					} else {
						setSelectedBearerData(null);
						setSelectedBearerId(0);
					}
				} else {
					setSelectedBearerData(null);
					setSelectedBearerId(0);
				}
				setIsLoading(false);
				if (reportLoadingState) {
					reportLoadingState(false);
				}
				if(signalBearerListLoaded) {
					signalBearerListLoaded(true);
				}
				if(signalNoBearersAvailable) {
					signalNoBearersAvailable(bearerList.length === 0);
				}
			} else {
				showTemporaryMessage("Fetch bearers FAILED!", res.msg, 'danger');
				handleInvalidToken();
				setIsLoading(false);
			if (reportLoadingState) {
				reportLoadingState(false);
			}				if(signalBearerListLoaded) {
					signalBearerListLoaded(true);
				}
			}
		} catch (err) {
			showTemporaryMessage("Fetch bearers FAILED!", err, 'danger');
			handleInvalidToken();
			setIsLoading(false);
		if (reportLoadingState) {
			reportLoadingState(false);
		}			if(signalBearerListLoaded) {
				signalBearerListLoaded(true);
			}
		}
	};

	useEffect(() => {
		updateListOfBearers();
	}, [userId, refreshBearerListTrigger]);

	const handleBearerChange = (bearer) => {
		setSelectedBearerData(bearer);
		setSelectedBearerId(bearer.id);
		if (reportBearerName) {
			reportBearerName(bearer.name);
		}
	};

	return (
		<>
			<div className="w-full mx-auto py-4">
				<h2 className="mb-2 text-3xl text-center font-bold">{/*t("bearers")*/}</h2> {/* TODO: Rename to: Existing bearers(?) to avoid repetation on headers */}
				<div className="flex flex-row gap-4">
					{/* Bearer details */}
					<div className="flex-1">
						<div className="basis-1/3">
							<Listbox
								as="div"
								value={selectedBearerData}
								onChange={handleBearerChange}>
								{({ open }) => (
									<>
										<div className="relative mt-2 mx-4">
											<Listbox.Button className="inline-flex w-full justify-between gap-x-1.5 rounded-md bg-white p-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed" disabled={isEditingBearer}>
												<span>
													{isLoading 
														? t("loading") + "..." 
														: bearers.length == 0 
															? t("no_bearers_registered") 
															: selectedBearerData 
																? selectedBearerData.name 
																: t("select_bearer")}
												</span>
												<ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
											</Listbox.Button>
											<Transition
												as={Fragment}
												enter="transition ease-out duration-100"
												enterFrom="transform opacity-0 scale-95"
												enterTo="transform opacity-100 scale-100"
												leave="transition ease-in duration-75"
												leaveFrom="transform opacity-100 scale-100"
												leaveTo="transform opacity-0 scale-95">
												<Listbox.Options
												className="absolute right-0 z-[9999] mt-1 w-full max-h-40 overflow-auto origin-top-right text-sm rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
													style={{ display: open ? 'block' : 'none' }}
													>
													{bearers.map((bearer) => (
														<Listbox.Option key={bearer.id} value={bearer}>
															{({ active }) => (
																<div
																	className={`${
																		active ? 'bg-gray-100' : ''
																			} cursor-pointer select-none relative py-2 pl-3 pr-9`}>
																	<span>{bearer.name}</span>
																</div>
															)}
														</Listbox.Option>
													))}
												</Listbox.Options>
											</Transition>
										</div>
									</>
								)}
							</Listbox>
						</div>
					</div>
				</div>
			</div>
		</>
	)
};

export default ListOfBearers;
